import React, { useState, useRef, useEffect } from 'react';
import './BankStatementViewer.css';

const BankStatementViewer = ({ results, file }) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [splitPercent, setSplitPercent] = useState(40);
  const [pdfUrl, setPdfUrl] = useState(null);
  const isDragging = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (file && file.type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (ev) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(70, Math.max(20, pct)));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
  const content = results.content || results || {};
  const creditAssessment = content.credit_assessment || {};
  const fraudDetection = content.fraud_detection || {};
  const cashFlow = content.cash_flow_analysis || {};
  const merchantAnalysis = content.merchant_analysis || {};
  const compliance = content.compliance_flags || content.aml_compliance || {};
  const insights = content.financial_insights || {};

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getScoreColor = (score) => {
    if (score >= 70) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getRiskColor = (level) => {
    const l = (level || '').toUpperCase();
    if (l === 'LOW') return '#10b981';
    if (l === 'MEDIUM') return '#f59e0b';
    return '#ef4444';
  };

  const fmt = (n) => n != null ? Number(n).toLocaleString('en-IN') : '0';

  const handleDownloadJSON = () => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bank_statement_${content.account_holder_name || content.account_holder || 'data'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    const txns = content.transactions || [];
    let csv = 'Date,Description,Category,Debit,Credit,Balance\n';
    txns.forEach(t => {
      const isDebit = (t.type || '').toLowerCase() === 'debit';
      csv += `"${t.date || ''}","${(t.description || '').replace(/"/g, '""')}","${t.category || ''}","${isDebit ? t.amount : ''}","${!isDebit ? t.amount : ''}","${t.balance || ''}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${content.account_holder_name || content.account_holder || 'data'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const eligScore = creditAssessment.eligibility_score ?? creditAssessment.score;
  const statPeriod = content.statement_period;
  const periodStr = statPeriod
    ? (typeof statPeriod === 'object' ? `${statPeriod.from || ''} to ${statPeriod.to || ''}` : statPeriod)
    : 'N/A';

  const analysisPanel = (
    <div className="bank-statement-viewer">
      <div className="statement-header">
        <div>
          <h2>🏦 Bank Statement Analysis</h2>
          <span className="statement-sub">{content.bank_name || ''} {content.account_number ? `• ****${String(content.account_number).slice(-4)}` : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {content.document_type && <span className="doc-type">{content.document_type}</span>}
          <button className="dl-btn" onClick={handleDownloadJSON}>⬇️ JSON</button>
          <button className="dl-btn" onClick={handleDownloadCSV}>⬇️ CSV</button>
        </div>
      </div>

      {/* Balance Summary Cards */}
      <div className="balance-section">
        <div className="balance-card">
          <label>Opening Balance</label>
          <span className="amount">₹{fmt(content.opening_balance)}</span>
        </div>
        <div className="balance-card">
          <label>Closing Balance</label>
          <span className="amount">₹{fmt(content.closing_balance)}</span>
        </div>
        <div className="balance-card">
          <label>Total Credits</label>
          <span className="amount credit">₹{fmt(content.total_credits || insights.total_credits)}</span>
        </div>
        <div className="balance-card">
          <label>Total Debits</label>
          <span className="amount debit">₹{fmt(content.total_debits || insights.total_debits)}</span>
        </div>
        {insights.net_flow != null && (
          <div className="balance-card">
            <label>Net Flow</label>
            <span className={`amount ${insights.net_flow >= 0 ? 'credit' : 'debit'}`}>₹{fmt(insights.net_flow)}</span>
          </div>
        )}
      </div>

      {/* Account Information */}
      <CollapsibleSection title="📋 Account Information" sectionId="account" isCollapsed={collapsedSections['account']} onToggle={() => toggleSection('account')}>
        <div className="info-grid">
          <InfoItem label="Account Holder" value={content.account_holder_name || content.account_holder} />
          <InfoItem label="Account Number" value={content.account_number} />
          <InfoItem label="Bank Name" value={content.bank_name} />
          <InfoItem label="IFSC Code" value={content.ifsc_code} />
          <InfoItem label="Branch" value={content.branch} />
          <InfoItem label="Statement Period" value={periodStr} />
          {content.mobile && <InfoItem label="Mobile" value={content.mobile} />}
          {content.email && <InfoItem label="Email" value={content.email} />}
          {content.pan && <InfoItem label="PAN" value={content.pan} />}
          {content.city && <InfoItem label="City" value={content.city} />}
          {content.state && <InfoItem label="State" value={content.state} />}
          {content.pincode && <InfoItem label="Pincode" value={content.pincode} />}
        </div>
      </CollapsibleSection>

      {/* Financial Insights */}
      {Object.keys(insights).length > 0 && (
        <CollapsibleSection title="📊 Financial Insights" sectionId="insights" isCollapsed={collapsedSections['insights']} onToggle={() => toggleSection('insights')}>
          <div className="info-grid">
            {insights.average_monthly_credit != null && <InfoItem label="Avg Monthly Credit" value={`₹${fmt(insights.average_monthly_credit)}`} />}
            {insights.average_monthly_debit != null && <InfoItem label="Avg Monthly Debit" value={`₹${fmt(insights.average_monthly_debit)}`} />}
            {insights.savings_rate != null && <InfoItem label="Savings Rate" value={`${insights.savings_rate}%`} />}
            {insights.bounce_rate != null && <InfoItem label="Bounce Rate" value={`${insights.bounce_rate}%`} />}
            {insights.emi_obligations != null && <InfoItem label="EMI Obligations" value={`₹${fmt(insights.emi_obligations)}`} />}
            {insights.inflow_stability && <InfoItem label="Inflow Stability" value={insights.inflow_stability} />}
            {insights.outflow_pattern && <InfoItem label="Outflow Pattern" value={insights.outflow_pattern} />}
          </div>
        </CollapsibleSection>
      )}

      {/* Credit Assessment */}
      {(eligScore != null || creditAssessment.recommendation) && (
        <CollapsibleSection title="💳 Credit Assessment" sectionId="credit" isCollapsed={collapsedSections['credit']} onToggle={() => toggleSection('credit')}>
          {eligScore != null && (
            <div className="score-card" style={{ borderColor: getScoreColor(eligScore) }}>
              <div className="score-circle" style={{ background: `conic-gradient(${getScoreColor(eligScore)} ${eligScore * 3.6}deg, #e5e7eb 0deg)` }}>
                <span className="score-value">{eligScore}</span>
                <span className="score-label">Score</span>
              </div>
              <div className="score-details">
                {creditAssessment.recommendation && <h4>{creditAssessment.recommendation}</h4>}
                <div className="metrics">
                  {creditAssessment.monthly_income != null && <Metric label="Monthly Income" value={`₹${fmt(creditAssessment.monthly_income)}`} />}
                  {creditAssessment.total_monthly_emi != null && <Metric label="Total EMI" value={`₹${fmt(creditAssessment.total_monthly_emi)}`} />}
                  {creditAssessment.debt_service_ratio != null && <Metric label="Debt Service Ratio" value={`${creditAssessment.debt_service_ratio}%`} danger={creditAssessment.debt_service_ratio > 50} />}
                  {creditAssessment.savings_rate != null && <Metric label="Savings Rate" value={`${creditAssessment.savings_rate}%`} />}
                  {creditAssessment.risk_category && <Metric label="Risk Category" value={creditAssessment.risk_category} />}
                  {creditAssessment.repayment_capacity && <Metric label="Repayment Capacity" value={creditAssessment.repayment_capacity} />}
                </div>
              </div>
            </div>
          )}

          <div className="eligibility-cards">
            {(creditAssessment.max_loan_eligibility != null || creditAssessment.recommended_loan_limit != null) && (
              <div className="elig-card">
                <label>Max Loan Eligibility</label>
                <span className="amount">₹{fmt(creditAssessment.max_loan_eligibility ?? creditAssessment.recommended_loan_limit)}</span>
              </div>
            )}
            {(creditAssessment.credit_card_limit_suggestion != null) && (
              <div className="elig-card">
                <label>Credit Card Limit</label>
                <span className="amount">₹{fmt(creditAssessment.credit_card_limit_suggestion)}</span>
              </div>
            )}
          </div>

          {creditAssessment.positive_indicators?.length > 0 && (
            <div className="indicators-block positive">
              <h4>✅ Positive Indicators</h4>
              <ul>{creditAssessment.positive_indicators.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </div>
          )}
          {(creditAssessment.risk_factors?.length > 0 || creditAssessment.negative_indicators?.length > 0) && (
            <div className="risk-factors">
              <h4>⚠️ Risk Factors</h4>
              <ul>
                {(creditAssessment.risk_factors || creditAssessment.negative_indicators || []).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Fraud Detection */}
      {(fraudDetection.fraud_detected != null || fraudDetection.risk_level) && (
        <CollapsibleSection title="🔍 Fraud Detection" sectionId="fraud" isCollapsed={collapsedSections['fraud']} onToggle={() => toggleSection('fraud')}>
          <div className={`fraud-card ${fraudDetection.fraud_detected ? 'alert' : 'safe'}`}>
            <div className="fraud-status">
              <span className="icon">{fraudDetection.fraud_detected ? '⚠️' : '✅'}</span>
              <span className="status">{fraudDetection.fraud_detected ? 'Suspicious Activity Detected' : 'No Fraud Detected'}</span>
            </div>
            <div className="fraud-score">
              <label>Fraud Risk Score</label>
              <span>{fraudDetection.fraud_score ?? fraudDetection.risk_level ?? '—'}{typeof fraudDetection.fraud_score === 'number' ? '/100' : ''}</span>
            </div>
            {fraudDetection.round_amount_transactions != null && (
              <div className="fraud-score">
                <label>Round Amount Transactions</label>
                <span>{fraudDetection.round_amount_transactions}</span>
              </div>
            )}
            {fraudDetection.unusual_hours_transactions != null && (
              <div className="fraud-score">
                <label>Unusual Hours Transactions</label>
                <span>{fraudDetection.unusual_hours_transactions}</span>
              </div>
            )}
            {(fraudDetection.flags?.length > 0 || fraudDetection.suspicious_patterns?.length > 0) && (
              <ul className="fraud-flags">
                {(fraudDetection.flags || fraudDetection.suspicious_patterns || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Cash Flow Analysis */}
      {(cashFlow.net_cash_flow != null || cashFlow.total_inflow != null || cashFlow.inflow_stability) && (
        <CollapsibleSection title="💰 Cash Flow Analysis" sectionId="cashflow" isCollapsed={collapsedSections['cashflow']} onToggle={() => toggleSection('cashflow')}>
          <div className="cashflow-grid">
            {cashFlow.total_inflow != null && <CfCard label="Total Inflow" value={`₹${fmt(cashFlow.total_inflow)}`} type="credit" />}
            {cashFlow.total_outflow != null && <CfCard label="Total Outflow" value={`₹${fmt(cashFlow.total_outflow)}`} type="debit" />}
            {cashFlow.net_cash_flow != null && <CfCard label="Net Cash Flow" value={`₹${fmt(cashFlow.net_cash_flow)}`} type={cashFlow.net_cash_flow >= 0 ? 'credit' : 'debit'} />}
            {cashFlow.runway_months != null && <CfCard label="Runway" value={`${cashFlow.runway_months} months`} />}
            {cashFlow.liquidity_score != null && <CfCard label="Liquidity Score" value={`${cashFlow.liquidity_score}/100`} />}
            {cashFlow.inflow_stability && <CfCard label="Inflow Stability" value={cashFlow.inflow_stability} />}
            {cashFlow.outflow_pattern && <CfCard label="Outflow Pattern" value={cashFlow.outflow_pattern} />}
          </div>
          {cashFlow.irregular_transactions?.length > 0 && (
            <div className="risk-factors" style={{ marginTop: 16 }}>
              <h4>⚡ Irregular Transactions</h4>
              <ul>{cashFlow.irregular_transactions.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Merchant / Spending Analysis */}
      {(merchantAnalysis.top_spending_categories?.length > 0 || merchantAnalysis.top_merchants?.length > 0) && (
        <CollapsibleSection title="🛍️ Spending Analysis" sectionId="merchant" isCollapsed={collapsedSections['merchant']} onToggle={() => toggleSection('merchant')}>
          {merchantAnalysis.lifestyle_type && (
            <div className="lifestyle-badge" style={{ background: merchantAnalysis.lifestyle_type === 'Luxury' ? '#f59e0b' : merchantAnalysis.lifestyle_type === 'Balanced' ? '#10b981' : '#6b7280' }}>
              {merchantAnalysis.lifestyle_type} Lifestyle
            </div>
          )}
          {merchantAnalysis.top_spending_categories?.length > 0 && (
            <div className="categories-list">
              {merchantAnalysis.top_spending_categories.map((cat, i) => (
                <div key={i} className="category-item">
                  <span className="cat-name">{cat.category}</span>
                  <span className="cat-amount">₹{fmt(cat.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {merchantAnalysis.top_merchants?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>Top Merchants</h4>
              <div className="categories-list">
                {merchantAnalysis.top_merchants.map((m, i) => (
                  <div key={i} className="category-item">
                    <span className="cat-name">🏪 {m}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Compliance / AML */}
      {(compliance.aml_risk_level || compliance.compliance_status || compliance.high_value_transaction_count != null) && (
        <CollapsibleSection title="⚖️ Compliance & AML" sectionId="compliance" isCollapsed={collapsedSections['compliance']} onToggle={() => toggleSection('compliance')}>
          <div className="compliance-card" style={{ borderColor: getRiskColor(compliance.aml_risk_level || compliance.compliance_status) }}>
            <div className="risk-badge" style={{ background: getRiskColor(compliance.aml_risk_level || compliance.compliance_status) }}>
              {(compliance.aml_risk_level || compliance.compliance_status || 'UNKNOWN').toUpperCase()}
            </div>
            <div className="compliance-details">
              {compliance.requires_kyc_verification != null && <CompItem label="KYC Verification Required" value={compliance.requires_kyc_verification ? 'Yes' : 'No'} />}
              {compliance.high_value_transaction_count != null && <CompItem label="High Value Transactions" value={compliance.high_value_transaction_count} />}
              {compliance.high_value_transactions != null && <CompItem label="High Value Transactions" value={compliance.high_value_transactions} />}
              {compliance.total_cash_transactions != null && <CompItem label="Total Cash Transactions" value={`₹${fmt(compliance.total_cash_transactions)}`} />}
              {compliance.cash_transactions != null && <CompItem label="Cash Transactions" value={compliance.cash_transactions} />}
            </div>
            {(compliance.compliance_flags?.length > 0 || compliance.flags?.length > 0) && (
              <ul className="compliance-flags">
                {(compliance.compliance_flags || compliance.flags || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Transactions Table */}
      {content.transactions?.length > 0 && (
        <CollapsibleSection title={`📊 All Transactions (${content.transactions.length})`} sectionId="transactions" isCollapsed={collapsedSections['transactions']} onToggle={() => toggleSection('transactions')}>
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {content.transactions.map((txn, idx) => {
                  const isDebit = (txn.type || '').toLowerCase() === 'debit';
                  const isCredit = (txn.type || '').toLowerCase() === 'credit';
                  return (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600, color: '#6b7280' }}>{idx + 1}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{txn.date}</td>
                      <td className="desc" title={txn.description}>{txn.description}</td>
                      <td><span className="category-badge">{txn.category || 'UNCATEGORIZED'}</span></td>
                      <td className={isDebit ? 'debit' : ''}>{isDebit ? `₹${fmt(txn.amount)}` : '—'}</td>
                      <td className={isCredit ? 'credit' : ''}>{isCredit ? `₹${fmt(txn.amount)}` : '—'}</td>
                      <td style={{ fontWeight: 700 }}>{txn.balance ? `₹${fmt(txn.balance)}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );

  if (pdfUrl) {
    return (
      <div ref={containerRef} className="bs-split-container" style={{ gridTemplateColumns: `${splitPercent}% 6px 1fr` }}>
        <div className="bs-pdf-panel">
          <div className="bs-pdf-header">🖼️ Source Document</div>
          <iframe src={pdfUrl} title="Bank Statement PDF" className="bs-pdf-iframe" />
        </div>
        <div className="bs-drag-handle" onMouseDown={handleMouseDown}>
          <div className="bs-drag-line" />
        </div>
        <div className="bs-analysis-panel">
          {analysisPanel}
        </div>
      </div>
    );
  }

  return analysisPanel;
};

const CollapsibleSection = ({ title, children, sectionId, isCollapsed, onToggle }) => (
  <div className="bank-section collapsible">
    <h3 className="section-header" onClick={onToggle}>
      {title}
      <span className="collapse-icon">{isCollapsed ? '▼' : '▲'}</span>
    </h3>
    {!isCollapsed && <div className="section-body">{children}</div>}
  </div>
);

const InfoItem = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="info-item">
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
};

const Metric = ({ label, value, danger }) => (
  <div className="metric">
    <label>{label}</label>
    <span className={danger ? 'high-risk' : ''}>{value}</span>
  </div>
);

const CfCard = ({ label, value, type }) => (
  <div className="cf-card">
    <label>{label}</label>
    <span className={`amount ${type || ''}`}>{value}</span>
  </div>
);

const CompItem = ({ label, value }) => (
  <div className="comp-item">
    <label>{label}</label>
    <span>{value}</span>
  </div>
);

export default BankStatementViewer;
